/**
 * Foods browser table component.
 *
 * Displays foods with macros and food groups for user browsing.
 * Data provided by connected wrapper via props.
 */

import * as stylex from "@stylexjs/stylex";

import type { NutritionFood } from "@animus/habit-modules/nutrition";
import { alert, spacing, table, text } from "@animus/primaris";

interface FoodsBrowserProps {
  foods: NutritionFood[];
  isLoading?: boolean;
  error?: string | null;
}

function FoodsBrowser({ foods, isLoading, error }: FoodsBrowserProps) {
  return (
    <div>
      <div {...stylex.props(spacing.mb6)}>
        <h2 {...stylex.props(text.bold, text["2xl"], text.white)}>Foods</h2>
        <p {...stylex.props(spacing.mt1, text.sm, text.muted)}>
          Browse the food database
        </p>
      </div>

      {error && <div {...stylex.props(alert.error, spacing.mb4)}>{error}</div>}

      {isLoading ? (
        <p {...stylex.props(text.muted)}>Loading foods...</p>
      ) : (
        <div {...stylex.props(table.container)}>
          <table {...stylex.props(table.table)}>
            <thead {...stylex.props(table.thead)}>
              <tr>
                <th {...stylex.props(table.th)}>Name</th>
                <th {...stylex.props(table.th)}>Calories</th>
                <th {...stylex.props(table.th)}>Protein</th>
                <th {...stylex.props(table.th)}>Carbs</th>
                <th {...stylex.props(table.th)}>Fat</th>
                <th {...stylex.props(table.th)}>Food Group</th>
              </tr>
            </thead>
            <tbody>
              {foods.map((food) => (
                <tr {...stylex.props(table.tr)} key={food.id}>
                  <td {...stylex.props(table.td, text.white)}>{food.name}</td>
                  <td {...stylex.props(table.td, text.muted)}>
                    {food.calories}
                  </td>
                  <td {...stylex.props(table.td, text.muted)}>
                    {food.protein}g
                  </td>
                  <td {...stylex.props(table.td, text.muted)}>{food.carbs}g</td>
                  <td {...stylex.props(table.td, text.muted)}>{food.fat}g</td>
                  <td {...stylex.props(table.td, text.muted)}>
                    {food.foodGroup ?? "-"}
                  </td>
                </tr>
              ))}
              {foods.length === 0 && (
                <tr>
                  <td {...stylex.props(table.empty)} colSpan={6}>
                    No foods found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export { FoodsBrowser, type FoodsBrowserProps };
